import React, { useEffect, useState } from 'react';
import { Bell, CheckCircle, X } from 'lucide-react';
import { Button } from './button';
import notificationService from '../../lib/notifications';

/**
 * NotificationPermissionBanner
 * Triggers on first app startup - allows users to opt-in to push notifications
 * Not blocking like GPS - user can dismiss
 */
const NotificationPermissionBanner = ({ onPermissionGranted, onDismissed }) => {
  const [showBanner, setShowBanner] = useState(false);
  const [requesting, setRequesting] = useState(false);

  useEffect(() => {
    checkAndShowBanner();
  }, []);

  const checkAndShowBanner = async () => {
    const hasPermission = await notificationService.hasPermission();

    if (hasPermission) {
      await notificationService.registerPushNotifications();
      const syncResult = await notificationService.syncStoredTokenWithBackend();
      setShowBanner(!syncResult.success);
      return;
    }

    setShowBanner(true);
  };

  const handleRequestPermission = async () => {
    setRequesting(true);
    const result = await notificationService.requestPermission();

    if (result.success) {
      setShowBanner(false);
      onPermissionGranted?.();
    } else {
      setShowBanner(true);
      onDismissed?.();
    }
    setRequesting(false);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    // Mark as checked even if dismissed
    localStorage.setItem('rtb_notification_permission_checked', new Date().toISOString());
    onDismissed?.();
  };

  if (!showBanner) {
    return null;
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-40 p-4 md:p-6">
      <div className="max-w-md mx-auto animate-in slide-in-from-top-2 fade-in duration-300">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg shadow-lg p-4 flex items-start gap-4">
          <Bell className="text-white flex-shrink-0 mt-1" size={24} />
          
          <div className="flex-1">
            <h3 className="text-white font-semibold mb-1">Stay Updated</h3>
            <p className="text-blue-100 text-sm mb-4">
              Receive instant notifications for task assignments, audit updates, and compliance alerts.
            </p>
            
            <div className="flex gap-2">
              <Button
                onClick={handleRequestPermission}
                disabled={requesting}
                className="flex-1 bg-white text-blue-700 hover:bg-blue-50 font-medium"
              >
                {requesting ? 'Enabling...' : 'Enable Notifications'}
              </Button>
              
              <button
                onClick={handleDismiss}
                className="p-2 hover:bg-blue-500/50 rounded transition text-white"
              >
                <X size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationPermissionBanner;
